import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ENDPOINT_PATH } from '../config';
import Footer from '../components/Footer';
import { uploadArtistArtwork, editArtist } from './artistHelper';
import { editArt, delArt } from './artHelper';
import logoImage from '../images/Logo-Base-basic.png';
import { Modal, Button, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import TextField from '@mui/material/TextField';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import './artistDashboard.css';
import { getArtReport } from './metricsHelper';
import '../App.css';

const ArtistNavbar = () => {
    return (
        <div className="navWrapper">
            <div className="AdminNav">
                <div>
                    <a href="/" className="NavLink">
                        <div className="AdminLogo d-flex justify-content-end">
                            <img src={logoImage} alt='logo' className="NavLogo" />
                        </div>
                        <div className="AdminLogoText font-2 ps-2 fontLogo">mycustomframe</div>
                    </a>
                </div>
            </div>
        </div>
    );
};

// Helper function to format timestamp to relative time
const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    let date;
    try {
        // Handle Firestore Timestamp
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else if (timestamp.seconds) {
            // Firestore Timestamp object with seconds property
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp._seconds) {
            // Alternative Firestore Timestamp format
            date = new Date(timestamp._seconds * 1000);
        } else {
            // Try parsing as regular date
            date = new Date(timestamp);
        }
        
        // Validate date
        if (isNaN(date.getTime())) {
            return 'Unknown time';
        }
    } catch (error) {
        console.error('Error parsing timestamp:', error, timestamp);
        return 'Unknown time';
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString();
};

// Helper function to get type badge color
const getTypeBadgeClass = (type) => {
    switch (type) {
        case 'rejected':
            return 'badge bg-danger';
        case 'removed':
            return 'badge bg-danger';
        case 'status_change':
            return 'badge bg-primary';
        default:
            return 'badge bg-secondary';
    }
};

// Helper function to get type label
const getNotificationTypeLabel = (type) => {
    switch (type) {
        case 'rejected':
            return 'Rejected';
        case 'removed':
            return 'Removed';
        case 'status_change':
            return 'Status Changed';
        default:
            return type;
    }
};

// Helper function to get type message
const getTypeMessage = (notification) => {
    const { type, oldStatus, newStatus, artworkName } = notification;
    
    if (type === 'rejected') {
        return `Your artwork "${artworkName}" has been rejected.`;
    } else if (type === 'removed') {
        return `Your artwork "${artworkName}" has been removed.`;
    } else if (type === 'status_change') {
        if (oldStatus === 'pending' && newStatus === 'approved') {
            return `Your artwork "${artworkName}" has been approved!`;
        } else {
            return `Your artwork "${artworkName}" status changed from ${oldStatus} to ${newStatus}.`;
        }
    }
    return `Update regarding "${artworkName}"`;
};

// NotificationItem Component
const NotificationItem = ({ notification, onMarkAsRead }) => {
    const isUnread = notification.read === false; // Explicit check for false
    const hasReason = notification.reason && notification.reason.trim() !== '';
    const [isReasonExpanded, setIsReasonExpanded] = useState(false);
    const MAX_REASON_PREVIEW_LENGTH = 100;
    
    return (
        <div className={`notification-item ${isUnread ? 'unread' : ''}`}>
            <div className="notification-content-wrapper">
                {isUnread && (
                    <button
                        className="btn btn-sm btn-primary notification-mark-read-btn"
                        onClick={() => onMarkAsRead(notification.id)}
                    >
                        Mark as Read
                    </button>
                )}
                <div className={`notification-content ${isUnread ? 'has-mark-read-btn' : ''}`}>
                    <div className="notification-header">
                        <span className={getTypeBadgeClass(notification.type)}>
                            {getNotificationTypeLabel(notification.type)}
                        </span>
                        <span className="notification-timestamp">
                            {formatRelativeTime(notification.createdAt)}
                        </span>
                    </div>
                    <p className="notification-message">
                        {getTypeMessage(notification)}
                    </p>
                    {hasReason && (
                        <div className={`notification-reason-box ${isUnread ? 'has-mark-read-btn' : ''}`}>
                            <strong className="notification-reason-label">Reason:</strong>
                            <p className="notification-reason-text">
                                {notification.reason.length > MAX_REASON_PREVIEW_LENGTH && !isReasonExpanded
                                    ? `${notification.reason.substring(0, MAX_REASON_PREVIEW_LENGTH)}...`
                                    : notification.reason
                                }
                            </p>
                            {notification.reason.length > MAX_REASON_PREVIEW_LENGTH && (
                                <button
                                    className="notification-reason-toggle"
                                    onClick={() => setIsReasonExpanded(!isReasonExpanded)}
                                >
                                    {isReasonExpanded ? 'Show less' : 'Show more'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ArtistDashboard = () => {
    const { artistId } = useParams(); // Get artistId from URL: /artists/:artistId/dashboard (undefined if legacy route)
    const navigate = useNavigate();
    
    const [currentTab, setCurrentTab] = useState('1');
    const [artworks, setArtworks] = useState([]);
    const [salesData, setSalesData] = useState(null);
    const [intervalPreset, setIntervalPreset] = useState('30'); // '7' | '30' | '90' | 'all' | 'custom'
    const [fromDate, setFromDate] = useState(null); // yyyy-mm-dd (applied range)
    const [toDate, setToDate] = useState(null);   // yyyy-mm-dd (applied range)
    const [customFromDate, setCustomFromDate] = useState(null); // pending input
    const [customToDate, setCustomToDate] = useState(null);     // pending input
    const [salesLoading, setSalesLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingArtwork, setEditingArtwork] = useState(null);
    const [artistInfo, setArtistInfo] = useState(null);
    
    // Form states for artwork upload
    const [artworkName, setArtworkName] = useState('');
    const [artDescription, setArtDescription] = useState('');
    const [category, setCategory] = useState('');
    const [artImage, setArtImage] = useState(null);
    const [artImagePreview, setArtImagePreview] = useState(null);
    const [categories, setCategories] = useState([]);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    
    // Edit form states
    const [editArtworkName, setEditArtworkName] = useState('');
    const [editArtDescription, setEditArtDescription] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editArtImage, setEditArtImage] = useState(null);
    const [editArtImagePreview, setEditArtImagePreview] = useState(null);
    
    // Profile editing states
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [profileFirstName, setProfileFirstName] = useState('');
    const [profileLastName, setProfileLastName] = useState('');
    const [profileEmail, setProfileEmail] = useState('');
    const [profilePhoneNumber, setProfilePhoneNumber] = useState('');
    const [profileBio, setProfileBio] = useState('');
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    
    // Notifications state
    const [notifications, setNotifications] = useState([]);
    
    // Calculate unread count from notifications (memoized)
    const unreadCount = useMemo(() => {
        return notifications.filter(notif => notif.read === false).length;
    }, [notifications]);
    
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    const userId = user?.uid || null; // Extract uid once - stable reference
    
    const hasFetchedData = useRef(false); // Prevent infinite loops
    const hasValidated = useRef(false); // Prevent validation loop
    
    // Handle legacy route: Redirect /artist-dashboard to /artists/:artistId/dashboard
    useEffect(() => {
        if (!artistId && userId) {
            // Legacy route accessed - fetch artistDocId and redirect to new URL
            fetch(ENDPOINT_PATH + `/api/get-user-info?user=` + userId)
                .then(res => res.json())
                .then(userData => {
                    if (userData.artistDocId) {
                        navigate(`/artists/${userData.artistDocId}/dashboard`, { replace: true });
                    } else {
                        alert('Artist profile not linked. Please contact admin.');
                        navigate('/');
                    }
                })
                .catch(error => {
                    console.error('Error fetching user info for redirect:', error);
                    navigate('/');
                });
            return;
        }
    }, [artistId, userId, navigate]);
    
    // Security validation: Ensure artist can only access their own dashboard
    useEffect(() => {
        // Skip validation if artistId is not available (legacy route - will redirect above)
        if (!artistId || !userId) {
            if (!userId) {
                alert('Please login to access the Artist Portal');
                navigate('/');
            }
            return;
        }
        
        // CRITICAL: Only validate once per artistId/userId combination
        const validationKey = `${userId}-${artistId}`;
        if (hasValidated.current === validationKey) {
            return; // Already validated
        }
        hasValidated.current = validationKey;
        
        // Verify user is an artist AND the artistId in URL matches their artistDocId
        Promise.all([
            fetch(ENDPOINT_PATH + `/api/get-account-type?user=` + userId).then(res => res.json()),
            fetch(ENDPOINT_PATH + `/api/get-user-info?user=` + userId).then(res => res.json())
        ])
        .then(([accountType, userData]) => {
            // Check if user is an artist
            if (accountType.toLowerCase() !== 'artist') {
                alert('Access denied: Artist account required');
                navigate('/');
                return;
            }
            
            // Validate that the artistId in URL matches the user's artistDocId
            if (userData.artistDocId !== artistId) {
                alert('Access denied: You can only access your own dashboard');
                navigate('/');
                return;
            }
        })
        .catch(error => {
            console.error("Error validating access:", error);
            navigate('/');
        });
    }, [userId, artistId, navigate]);
    
    // Fetch artist's data and artworks using artistId from URL
    useEffect(() => {
        // Early return if artistId is not available (should not happen if route is correct)
        if (!artistId) {
            console.error('Artist ID not found in URL');
            setLoading(false);
            return;
        }

        // Early return if user is not available yet
        if (!userId) {
            return;
        }

        // CRITICAL: Prevent infinite loops - only fetch once per artistId
        const fetchKey = `${userId}-${artistId}`;
        if (hasFetchedData.current === fetchKey) {
            return; // Already fetched for this artistId/userId combination
        }

        hasFetchedData.current = fetchKey; // Mark as fetched

        const fetchArtistData = async () => {
            try {
                setLoading(true);
                
                // Use artistId directly from URL (no need to fetch artistDocId - it's already in the URL!)
                
                // Fetch artist data directly using artistId from URL
                const artistResponse = await fetch(ENDPOINT_PATH + `/api/get-artist-data?artistDocId=${artistId}`);
                const artistData = await artistResponse.json();
                
                if (artistData && artistData.data) {
                    setArtistInfo(artistData.data);
                    
                    // Get artist's artwork (artArray is included in the same response)
                    if (artistData.data.artArray) {
                        setArtworks(artistData.data.artArray);
                    }
                } else {
                    console.warn("No artist profile found for artistId:", artistId);
                    setArtistInfo(null);
                }
                
                setLoading(false);
            } catch (error) {
                console.error('Error fetching artist data:', error);
                setLoading(false);
                hasFetchedData.current = null; // Reset on error so it can retry
            }
        };
        
        const fetchCategories = async () => {
            try {
                const res = await fetch(ENDPOINT_PATH + '/api/get-categories');
                const data = await res.json();
                if (data.data) {
                    setCategories(data.data);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        
        fetchArtistData();
        fetchCategories();
    }, [artistId, userId]); // Use userId (stable) instead of user object to prevent infinite loops
    
    // Fetch notifications for the artist
    useEffect(() => {
        if (!userId) {
            return; // Wait for userId to be available
        }

        const fetchNotifications = async () => {
            try {
                const response = await fetch(ENDPOINT_PATH + `/api/get-notifications?artistUid=${userId}`);
                const data = await response.json();
                
                if (response.ok && data.success) {
                    setNotifications(data.data || []);
                } else {
                    console.error('Error fetching notifications:', data.message);
                    setNotifications([]);
                }
            } catch (error) {
                console.error('Error fetching notifications:', error);
                setNotifications([]);
            }
        };

        fetchNotifications();
    }, [userId]);
    
    const handleTabChange = useCallback((event, newValue) => {
        setCurrentTab(newValue);
    }, []);
    
    // Handle marking notification as read
    const handleMarkAsRead = useCallback(async (notificationId) => {
        if (!userId) {
            console.error('User ID not available');
            return;
        }

        try {
            const response = await fetch(ENDPOINT_PATH + '/api/mark-notification-read', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    notificationId: notificationId,
                    artistUid: userId
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Update local state - mark notification as read
                setNotifications((prevNotifications) =>
                    prevNotifications.map((notif) =>
                        notif.id === notificationId
                            ? { ...notif, read: true }
                            : notif
                    )
                );
                // Note: unreadCount will automatically recalculate via useMemo
            } else {
                console.error('Error marking notification as read:', data.message);
                alert(`Error: ${data.message || 'Failed to mark notification as read'}`);
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
            alert('Error marking notification as read. Please try again.');
        }
    }, [userId]);
    
    // Helpers for date handling
    const formatYMD = (d) => d.toISOString().slice(0,10);
    const presetToRange = (preset) => {
        const today = new Date();
        const end = formatYMD(today);
        const start = new Date();
        if (preset === '7') { start.setDate(today.getDate() - 6); return { start: formatYMD(start), end }; }
        if (preset === '30') { start.setDate(today.getDate() - 29); return { start: formatYMD(start), end }; }
        if (preset === '90') { start.setDate(today.getDate() - 89); return { start: formatYMD(start), end }; }
        return { start: null, end: null };
    };
    // Strict YYYY-MM-DD validator (year must be exactly 4 digits)
    const isValidYMD = (val) => /^\d{4}-\d{2}-\d{2}$/.test(val);
    const setPendingFrom = (val) => setCustomFromDate(val || '');
    const setPendingTo = (val) => setCustomToDate(val || '');
    const applyCustomRange = () => {
        if (customFromDate && customToDate && isValidYMD(customFromDate) && isValidYMD(customToDate) && customFromDate <= customToDate) {
            setFromDate(customFromDate);
            setToDate(customToDate);
            setIntervalPreset('custom');
        }
    };
    
    // Fetch sales data when tab or filter changes
    useEffect(() => {
        const fetchSales = async () => {
            if (currentTab !== '2' || !artistId) return;
            setSalesLoading(true);
            try {
                let report;
                if (intervalPreset === 'all') {
                    report = await getArtReport(artistId);
                } else if (intervalPreset === 'custom' && fromDate && toDate) {
                    report = await getArtReport(artistId, fromDate, toDate);
                } else {
                    const range = presetToRange(intervalPreset);
                    if (range.start && range.end) {
                        report = await getArtReport(artistId, range.start, range.end);
                    } else {
                        report = await getArtReport(artistId);
                    }
                }
                setSalesData(report);
            } catch (e) {
                console.error('Error fetching art sales report', e);
                setSalesData(null);
            } finally {
                setSalesLoading(false);
            }
        };
        fetchSales();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTab, artistId, intervalPreset, fromDate, toDate]);
    
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setArtImage(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setArtImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    
    const handleUploadArtwork = async () => {
        // Validate required fields
        if (!artworkName.trim()) {
            alert('Please enter an artwork name.');
            return;
        }
        
        if (!category) {
            alert('Please select a category.');
            return;
        }
        
        if (!artImage) {
            alert('Please select an image file.');
            return;
        }
        
        setUploadLoading(true);
        
        try {
            // Call the upload API
            const uploadResult = await uploadArtistArtwork(
                userId,
                artworkName.trim(),
                category,
                0,
                artDescription.trim(),
                artImage
            );

            // Send artwork submission emails to artist and admins
            try {
                await fetch(`${ENDPOINT_PATH}/api/artwork-submission-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        artistUid: userId,
                        artistDocId: artistId,
                        artworkName: artworkName.trim()
                    })
                });
            } catch (emailError) {
                console.error('Error sending artwork submission emails:', emailError);
                // Do not block the upload if email fails
            }
            
            // Show success message
            alert(`Artwork uploaded successfully! It is pending admin approval.`);
            
            // Reset form
            setArtworkName('');
            setArtDescription('');
            setCategory('');
            setArtImage(null);
            setArtImagePreview(null);
            setShowUploadModal(false);
            
            // Refresh artwork list by re-fetching artist data
            if (artistId) {
                try {
                    const artistResponse = await fetch(ENDPOINT_PATH + `/api/get-artist-data?artistDocId=${artistId}`);
                    const artistData = await artistResponse.json();
                    
                    if (artistData && artistData.data && artistData.data.artArray) {
                        setArtworks(artistData.data.artArray);
                    }
                } catch (refreshError) {
                    console.error('Error refreshing artwork list:', refreshError);
                }
            }
            
        } catch (error) {
            console.error("Error uploading artwork:", error);
            const errorMessage = error.message || "Error uploading artwork. Please try again.";
            alert(errorMessage);
        } finally {
            setUploadLoading(false);
        }
    };
    
    const getStatusBadgeClass = (status) => {
        switch(status?.toLowerCase()) {
            case 'approved': return 'badge bg-success';
            case 'pending': return 'badge bg-warning text-dark';
            case 'rejected': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    };

    // Handle opening edit modal
    const handleEditArtwork = (artwork) => {
        setEditingArtwork(artwork);
        setEditArtworkName(artwork.artworkName || '');
        setEditArtDescription(artwork.artDescription || '');
        setEditCategory(artwork.category || '');
        setEditArtImage(null);
        setEditArtImagePreview(artwork.artImageUrl || null);
        setShowEditModal(true);
    };

    // Handle closing edit modal
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingArtwork(null);
        setEditArtworkName('');
        setEditArtDescription('');
        setEditCategory('');
        setEditArtImage(null);
        setEditArtImagePreview(null);
    };

    // Handle editing artwork image upload
    const handleEditImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditArtImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditArtImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle saving edited artwork
    const handleSaveEditArtwork = async () => {
        if (!editingArtwork) return;

        // Validate required fields
        if (!editArtworkName.trim()) {
            alert('Please enter an artwork name.');
            return;
        }

        if (!editCategory) {
            alert('Please select a category.');
            return;
        }

        setEditLoading(true);

        try {
            const currentStatus = (editingArtwork.status || 'pending').toLowerCase();
            const wasApproved = currentStatus === 'approved';
            const wasRejected = currentStatus === 'rejected';

            // Prepare updates object
            const updates = {
                artworkName: editArtworkName.trim(),
                category: editCategory,
                artDescription: editArtDescription.trim()
            };

            // If artwork was approved or rejected, set status back to pending for admin re-review
            // Pending artwork stays pending (no change needed)
            if (wasApproved || wasRejected) {
                updates.status = 'pending';
            }

            // Get artist name for editArt function
            const artistName = editingArtwork.artistName || `${artistInfo?.firstName} ${artistInfo?.lastName}`;

            // Call editArt helper
            await editArt(
                editingArtwork.id || editingArtwork.artDocID,
                editingArtwork.artistDocID,
                artistName,
                editArtImage,
                updates
            );

            // Show success message
            if (wasApproved) {
                alert(`Artwork updated successfully! Status changed to Pending for admin review.`);
            } else if (wasRejected) {
                alert(`Artwork updated successfully! Status changed to Pending for admin review.`);
            } else {
                alert('Artwork updated successfully!');
            }

            // Close modal and reset form
            handleCloseEditModal();

            // Refresh artwork list
            if (artistId) {
                try {
                    const artistResponse = await fetch(ENDPOINT_PATH + `/api/get-artist-data?artistDocId=${artistId}`);
                    const artistData = await artistResponse.json();

                    if (artistData && artistData.data && artistData.data.artArray) {
                        setArtworks(artistData.data.artArray);
                    }
                } catch (refreshError) {
                    console.error('Error refreshing artwork list:', refreshError);
                }
            }
        } catch (error) {
            console.error('Error updating artwork:', error);
            alert(`Error updating artwork: ${error.message || 'Please try again.'}`);
        } finally {
            setEditLoading(false);
        }
    };

    // Initialize profile editing state when artistInfo changes
    useEffect(() => {
        if (artistInfo && !isEditingProfile) {
            setProfileFirstName(artistInfo.firstName || '');
            setProfileLastName(artistInfo.lastName || '');
            setProfileEmail(artistInfo.email || '');
            setProfilePhoneNumber(artistInfo.phoneNumber || '');
            setProfileBio(artistInfo.artistBio || '');
            setProfilePicturePreview(artistInfo.artistPictureURL || null);
        }
    }, [artistInfo, isEditingProfile]);

    // Handle profile picture upload
    const handleProfilePictureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePicture(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle starting profile edit
    const handleStartEditProfile = () => {
        setIsEditingProfile(true);
    };

    // Handle canceling profile edit
    const handleCancelEditProfile = () => {
        setIsEditingProfile(false);
        // Reset to original values
        if (artistInfo) {
            setProfileFirstName(artistInfo.firstName || '');
            setProfileLastName(artistInfo.lastName || '');
            setProfileEmail(artistInfo.email || '');
            setProfilePhoneNumber(artistInfo.phoneNumber || '');
            setProfileBio(artistInfo.artistBio || '');
            setProfilePicture(null);
            setProfilePicturePreview(artistInfo.artistPictureURL || null);
        }
    };

    // Handle saving profile changes
    const handleSaveProfile = async () => {
        if (!artistId || !artistInfo) {
            alert('Error: Artist profile not found. Please refresh the page.');
            return;
        }

        // Validate required fields
        if (!profileFirstName.trim()) {
            alert('Please enter your first name.');
            return;
        }

        if (!profileLastName.trim()) {
            alert('Please enter your last name.');
            return;
        }

        if (!profileEmail.trim()) {
            alert('Please enter your email.');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(profileEmail.trim())) {
            alert('Please enter a valid email address.');
            return;
        }

        setProfileLoading(true);

        try {
            // Prepare update object
            const update = {
                firstName: profileFirstName.trim(),
                lastName: profileLastName.trim(),
                email: profileEmail.trim(),
                phoneNumber: profilePhoneNumber.trim(),
                artistBio: profileBio.trim()
            };

            // Call editArtist API
            await editArtist(artistId, update, profilePicture);

            // Show success message
            alert('Profile updated successfully!');

            // Exit edit mode
            setIsEditingProfile(false);
            setProfilePicture(null); // Clear uploaded file

            // Refresh artist data
            if (artistId) {
                try {
                    const artistResponse = await fetch(ENDPOINT_PATH + `/api/get-artist-data?artistDocId=${artistId}`);
                    const artistData = await artistResponse.json();

                    if (artistData && artistData.data) {
                        setArtistInfo(artistData.data);
                    }
                } catch (refreshError) {
                    console.error('Error refreshing artist data:', refreshError);
                    // Don't show error - profile update was successful
                }
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alert(`Error updating profile: ${error.message || 'Please try again.'}`);
        } finally {
            setProfileLoading(false);
        }
    };

    // Handle deleting artwork
    const handleDeleteArtwork = async (artwork) => {
        const artworkStatus = (artwork.status || 'pending').toLowerCase();
        const isApproved = artworkStatus === 'approved';

        if (isApproved) {
            // Should not happen if button is disabled, but double-check
            alert('Cannot delete approved artwork. Contact admin if needed.');
            return;
        }

        const confirmed = window.confirm(`Are you sure you want to delete "${artwork.artworkName}"? This action cannot be undone.`);
        
        if (!confirmed) {
            return;
        }

        try {
            // Use the artwork ID (now included from backend)
            const artDocId = artwork.id || artwork.artDocID;
            const artistDocId = artwork.artistDocID;
            
            if (!artDocId || !artistDocId) {
                alert('Error: Missing artwork identification. Please refresh the page and try again.');
                return;
            }

            // Call delete API
            await delArt(artistDocId, artDocId);
            
            // Update local state - remove deleted artwork
            setArtworks((prevArtworks) =>
                prevArtworks.filter((a) => {
                    const aId = a.id || a.artDocID;
                    return aId !== artDocId;
                })
            );

            alert(`Artwork "${artwork.artworkName}" has been deleted.`);
        } catch (error) {
            console.error('Error deleting artwork:', error);
            alert(`Error deleting artwork: ${error.message || 'Please try again.'}`);
        }
    };
    
    return (
        <div>
            <ArtistNavbar />
            <hr className="admin-divider" />
            
            <div className="artist-dashboard-container">
                <div className="dashboard-header">
                    <h2>🎨 Artist Portal</h2>
                    <p className="text-muted">Manage your artwork and track your sales</p>
                </div>
                
                <Box sx={{ width: '100%', typography: 'body1' }}>
                    <TabContext value={currentTab}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList onChange={handleTabChange} aria-label="artist dashboard tabs">
                                <Tab label="My Artwork" value="1" />
                                <Tab label="Sales & Commissions" value="2" />
                                <Tab label="Profile" value="3" />
                                <Tab 
                                    label={unreadCount > 0 ? `Notifications (${unreadCount})` : "Notifications"} 
                                    value="4" 
                                />
                            </TabList>
                        </Box>
                        
                        {/* Tab 1: My Artwork */}
                        <TabPanel value="1">
                            <div className="artwork-section">
                                <div className="section-header">
                                    <h4>My Artwork Gallery</h4>
                                    <Button 
                                        variant="primary" 
                                        onClick={() => setShowUploadModal(true)}
                                        className=""
                                    >
                                        + Upload New Artwork
                                    </Button>
                                </div>
                                
                                {loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3">Loading your artwork...</p>
                                    </div>
                                ) : artworks.length === 0 ? (
                                    <div className="empty-state">
                                        <h5>No artwork uploaded yet</h5>
                                        <p className="text-muted">Upload your first piece to get started!</p>
                                    </div>
                                ) : (
                                    <div className="artwork-grid">
                                        <Table striped bordered hover responsive>
                                            <thead>
                                                <tr>
                                                    <th>Image</th>
                                                    <th>Artwork Name</th>
                                                    <th>Category</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {artworks.map((art, index) => (
                                                    <tr key={art.id || index}>
                                                        <td>
                                                            <img 
                                                                src={art.artImageUrl} 
                                                                alt={art.artworkName}
                                                                className="artwork-thumbnail"
                                                            />
                                                        </td>
                                                        <td>{art.artworkName}</td>
                                                        <td>{art.category}</td>
                                                        <td>
                                                            <span className={getStatusBadgeClass(art.status)}>
                                                                {art.status || 'Published'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="outline-primary" 
                                                                    onClick={() => handleEditArtwork(art)}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                {(art.status || 'pending').toLowerCase() === 'approved' ? (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={
                                                                            <Tooltip id={`tooltip-delete-${art.id || art.artDocID}`}>
                                                                                Cannot delete approved artwork. Contact admin if needed.
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <span>
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline-danger" 
                                                                                disabled
                                                                                style={{ cursor: 'not-allowed', opacity: 0.6 }}
                                                                            >
                                                                                Delete
                                                                            </Button>
                                                                        </span>
                                                                    </OverlayTrigger>
                                                                ) : (
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="outline-danger"
                                                                        onClick={() => handleDeleteArtwork(art)}
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                        
                        {/* Tab 2: Sales & Commissions */}
                        <TabPanel value="2">
                            <div className="sales-section">
                                <h4>💰 Sales & Commission Reports</h4>
                                <p className="text-muted">Track which of your artworks sold and your earnings</p>
                                
                                <div className="commission-summary">
                                    <div className="stat-card">
                                        <h5>Total Artworks Sold</h5>
                                        <p className="stat-number">{salesData?.totalQuantity ?? 0}</p>
                                        <small className="text-muted">All time</small>
                                    </div>
                                    <div className="stat-card">
                                        <h5>Total Commission Earned</h5>
                                        <p className="stat-number">${(artistInfo?.totalEarned ?? 0).toFixed(2)}</p>
                                        <small className="text-muted">All time</small>
                                    </div>
                                    <div className="stat-card">
                                        <h5>Pending Artworks</h5>
                                        <p className="stat-number">{artworks.filter(a => a.status === 'pending').length}</p>
                                        <small className="text-muted">Awaiting approval</small>
                                    </div>
                                    <div className="stat-card">
                                        <h5>Approved Artworks</h5>
                                        <p className="stat-number">{artworks.filter(a => a.status === 'approved').length}</p>
                                        <small className="text-muted">Live in gallery</small>
                                    </div>
                                </div>
                                
                                <div className="sales-filters">
                                    <div className="preset-group btn-group" role="group" aria-label="interval presets">
                                        <button className={`preset-btn btn ${intervalPreset==='7'?'active':''}`} onClick={()=>setIntervalPreset('7')} aria-pressed={intervalPreset==='7'}>Last 7 days</button>
                                        <button className={`preset-btn btn ${intervalPreset==='30'?'active':''}`} onClick={()=>setIntervalPreset('30')} aria-pressed={intervalPreset==='30'}>Last 30 days</button>
                                        <button className={`preset-btn btn ${intervalPreset==='90'?'active':''}`} onClick={()=>setIntervalPreset('90')} aria-pressed={intervalPreset==='90'}>Last 90 days</button>
                                        <button className={`preset-btn btn ${intervalPreset==='all'?'active':''}`} onClick={()=>setIntervalPreset('all')} aria-pressed={intervalPreset==='all'}>All‑time</button>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <input type="date"
                                            className="form-control"
                                            value={customFromDate||''}
                                            onChange={(e)=>setPendingFrom(e.target.value)}
                                            min="2000-01-01"
                                            max="2099-12-31"
                                            pattern="\\d{4}-\\d{2}-\\d{2}" />
                                        <span>to</span>
                                        <input type="date"
                                            className="form-control"
                                            value={customToDate||''}
                                            onChange={(e)=>setPendingTo(e.target.value)}
                                            min="2000-01-01"
                                            max="2099-12-31"
                                            pattern="\\d{4}-\\d{2}-\\d{2}" />
                                        <button
                                            className="apply-btn btn"
                                            onClick={applyCustomRange}
                                            disabled={!customFromDate || !customToDate || !isValidYMD(customFromDate) || !isValidYMD(customToDate) || customFromDate>customToDate}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                                
                                
                                <h4 className="sales-table-title">Sales by Artwork</h4>
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Image</th>
                                            <th>Artwork Name</th>
                                            <th>Times Sold</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {salesLoading ? (
                                            <tr><td colSpan="3" className="text-center p-4">Loading...</td></tr>
                                        ) : salesData && salesData.artMap && Object.keys(salesData.artMap).length > 0 ? (
                                            Object.entries(salesData.artMap).map(([key, artwork]) => (
                                                <tr key={key}>
                                                    <td><img src={artwork.artImageUrl} alt="" style={{ width: 64, height: 64, objectFit: 'cover' }}/></td>
                                                    <td>{artwork.artworkName}</td>
                                                    <td>{artwork.totalQuantity}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="text-center text-muted p-4">
                                                    No sales in this period yet.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </TabPanel>
                        
                        {/* Tab 3: Profile */}
                        <TabPanel value="3">
                            <div className="profile-section">
                                <h4>👤 Artist Profile</h4>
                                <p className="text-muted">Manage your public artist profile</p>
                                
                                {artistInfo ? (
                                    <div className="profile-content">
                                        <div className="profile-picture-section">
                                            {profilePicturePreview && (
                                                <img 
                                                    src={profilePicturePreview} 
                                                    alt="Artist Profile" 
                                                    className="artist-profile-pic"
                                                />
                                            )}
                                            {isEditingProfile ? (
                                                <>
                                                    <input 
                                                        type="file" 
                                                        className="form-control mt-3"
                                                        accept="image/*"
                                                        onChange={handleProfilePictureUpload}
                                                        style={{ maxWidth: '300px', margin: '0 auto' }}
                                                    />
                                                    <small className="text-muted d-block mt-2">
                                                        Upload a new profile picture (optional)
                                                    </small>
                                                </>
                                            ) : (
                                                <div className="mt-3">
                                                    <Button 
                                                        variant="outline-primary" 
                                                        onClick={handleStartEditProfile}
                                                    >
                                                        Edit Profile
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="profile-info">
                                            <div className="mb-3">
                                                <TextField
                                                    fullWidth
                                                    label="First Name"
                                                    value={profileFirstName}
                                                    onChange={(e) => setProfileFirstName(e.target.value)}
                                                    variant="outlined"
                                                    disabled={!isEditingProfile}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <TextField
                                                    fullWidth
                                                    label="Last Name"
                                                    value={profileLastName}
                                                    onChange={(e) => setProfileLastName(e.target.value)}
                                                    variant="outlined"
                                                    disabled={!isEditingProfile}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <TextField
                                                    fullWidth
                                                    label="Email"
                                                    type="email"
                                                    value={profileEmail}
                                                    onChange={(e) => setProfileEmail(e.target.value)}
                                                    variant="outlined"
                                                    disabled={!isEditingProfile}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <TextField
                                                    fullWidth
                                                    label="Phone Number"
                                                    value={profilePhoneNumber}
                                                    onChange={(e) => setProfilePhoneNumber(e.target.value)}
                                                    variant="outlined"
                                                    disabled={!isEditingProfile}
                                                    placeholder="(XXX) XXX-XXXX"
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <TextField
                                                    fullWidth
                                                    label="Artist Bio"
                                                    multiline
                                                    rows={4}
                                                    value={profileBio}
                                                    onChange={(e) => setProfileBio(e.target.value)}
                                                    variant="outlined"
                                                    disabled={!isEditingProfile}
                                                    placeholder="Tell us about yourself, your artistic style, inspiration, etc."
                                                />
                                            </div>
                                            
                                            {isEditingProfile ? (
                                                <div className="d-flex gap-2 mt-3">
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={handleSaveProfile}
                                                        disabled={profileLoading || !profileFirstName || !profileLastName || !profileEmail}
                                                    >
                                                        {profileLoading ? 'Saving...' : 'Save Changes'}
                                                    </Button>
                                                    <Button 
                                                        variant="secondary" 
                                                        onClick={handleCancelEditProfile}
                                                        disabled={profileLoading}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <>
                                                    {artistId && (
                                                        <div className="mt-3">
                                                            <p><strong>🌐 Public Profile:</strong></p>
                                                            <a 
                                                                href={`/artistProfilePage/${artistId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline-secondary"
                                                            >
                                                                View Your Public Profile
                                                            </a>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="alert alert-warning">
                                        <strong>⚠️ No Artist Profile Found</strong>
                                        <p>Your artist profile hasn't been linked yet. Please contact an administrator.</p>
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                        
                        {/* Tab 4: Notifications */}
                        <TabPanel value="4">
                            <div className="notifications-section" style={{ marginTop: '20px' }}>
                                <div className="section-header">
                                    <h4>🔔 Notifications</h4>
                                </div>
                                {notifications.length === 0 ? (
                                    <div className="alert alert-info">
                                        <p>No notifications yet.</p>
                                    </div>
                                ) : (
                                    <div className="notifications-list">
                                        {notifications.map((notification) => (
                                            <NotificationItem
                                                key={notification.id}
                                                notification={notification}
                                                onMarkAsRead={handleMarkAsRead}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                    </TabContext>
                </Box>
            </div>
            
            {/* Upload Artwork Modal */}
            <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>📤 Upload New Artwork</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <input 
                            type="file" 
                            className="form-control"
                            accept="image/*"
                            onChange={handleImageUpload}
                        />
                        {artImagePreview && (
                            <div className="mt-3 text-center">
                                <img 
                                    src={artImagePreview} 
                                    alt="Preview" 
                                    style={{maxWidth: '100%', maxHeight: '300px', objectFit: 'contain'}}
                                />
                            </div>
                        )}
                    </div>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Artwork Name"
                            value={artworkName}
                            onChange={(e) => setArtworkName(e.target.value)}
                            variant="outlined"
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={4}
                            value={artDescription}
                            onChange={(e) => setArtDescription(e.target.value)}
                            variant="outlined"
                            placeholder="Describe your artwork, inspiration, technique, etc."
                        />
                    </div>
                    <div className="mb-3">
                        <select 
                            className="form-select"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            required
                        >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                                <option key={cat.category} value={cat.category}>
                                    {cat.category}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Price removed from artist upload; admin controls price */}
                    <div className="alert alert-info">
                        <small>
                            <strong>ℹ️ Note:</strong> Your artwork will be submitted for admin review. 
                            It will appear in the public gallery once approved.
                        </small>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowUploadModal(false)}
                        disabled={uploadLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleUploadArtwork}
                        disabled={uploadLoading || !artworkName || !category || !artImage}
                    >
                        {uploadLoading ? 'Uploading...' : 'Submit for Review'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Artwork Modal */}
            <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>✏️ Edit Artwork</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <input 
                            type="file" 
                            className="form-control"
                            accept="image/*"
                            onChange={handleEditImageUpload}
                        />
                        {editArtImagePreview && (
                            <div className="mt-3 text-center">
                                <img 
                                    src={editArtImagePreview} 
                                    alt="Preview" 
                                    style={{maxWidth: '100%', maxHeight: '300px', objectFit: 'contain'}}
                                />
                            </div>
                        )}
                    </div>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Artwork Name"
                            value={editArtworkName}
                            onChange={(e) => setEditArtworkName(e.target.value)}
                            variant="outlined"
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={4}
                            value={editArtDescription}
                            onChange={(e) => setEditArtDescription(e.target.value)}
                            variant="outlined"
                            placeholder="Describe your artwork, inspiration, technique, etc."
                        />
                    </div>
                    <div className="mb-3">
                        <select 
                            className="form-select"
                            value={editCategory}
                            onChange={(e) => setEditCategory(e.target.value)}
                            required
                        >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                                <option key={cat.category} value={cat.category}>
                                    {cat.category}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Price editing removed; admin controls price */}
                    {editingArtwork && ((editingArtwork.status || 'pending').toLowerCase() === 'approved' || (editingArtwork.status || 'pending').toLowerCase() === 'rejected') && (
                        <div className="alert alert-warning">
                            <small>
                                <strong>⚠️ Note:</strong> Editing {editingArtwork.status === 'approved' ? 'approved' : 'rejected'} artwork will change its status back to "Pending" for admin review.
                            </small>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={handleCloseEditModal}
                        disabled={editLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveEditArtwork}
                        disabled={editLoading || !editArtworkName || !editCategory}
                    >
                        {editLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            <Footer />
        </div>
    );
};

export default ArtistDashboard;

